import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Alert, Typography, Button } from '@mui/material';

interface Props {
  children: ReactNode;
  location?: { pathname: string }; // ルート変更検出用
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorId: string | null;
}

/**
 * グローバルエラーハンドラー
 *
 * アプリケーション全体で発生した予期しないエラーをキャッチし、
 * ユーザーフレンドリーなエラー画面を表示する
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorId: null };
  }

  /**
   * エラー発生時に呼ばれる静的メソッド
   *
   * 注意: このメソッドは純粋関数である必要があります
   * 副作用（ランダム値生成、外部API呼び出しなど）は禁止
   */
  static getDerivedStateFromError(error: Error): State {
    // エラー状態のみ設定、errorIdはcomponentDidCatchで生成
    return { hasError: true, error, errorId: null };
  }

  componentDidUpdate(prevProps: Props): void {
    // ルート変更時にエラー状態をリセット
    if (this.state.hasError && prevProps.location?.pathname !== this.props.location?.pathname) {
      this.setState({ hasError: false, error: null, errorId: null });
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // エラー追跡用のユニークID生成: ERR_[timestamp]_[uuid]形式
    // crypto.randomUUID()は安全なコンテキスト（HTTPS, localhost等）で利用可能
    let uuid: string;
    try {
      uuid = crypto.randomUUID();
    } catch (cryptoError) {
      // crypto APIが利用できない場合のフォールバック
      console.warn('⚠️ Crypto API利用不可、フォールバックを使用:', {
        errorId: 'CRYPTO_API_UNAVAILABLE',
        reason: cryptoError instanceof Error ? cryptoError.message : String(cryptoError),
        isSecureContext: window.isSecureContext,
        protocol: window.location.protocol,
        timestamp: new Date().toISOString()
      });
      // 擬似ランダム文字列を生成（UUIDではないが一意性は確保）
      uuid = Math.random().toString(36).substring(2) + Math.random().toString(36).substring(2);
    }

    const errorId = `ERR_${Date.now()}_${uuid}`;

    // errorIdをstateに設定
    this.setState({ errorId });

    // 構造化ログ出力
    console.error('❌ アプリケーションエラー:', {
      errorId,
      errorType: error.constructor.name,
      message: error.message,
      timestamp: new Date().toISOString()
    });

    if (process.env.NODE_ENV === 'development') {
      console.error('詳細:', { error, errorInfo });
    }

    // 本番環境: エラートラッキングサービスへ送信
    if (process.env.NODE_ENV === 'production') {
      this.logErrorToService(error, errorInfo, errorId);
    }
  }

  /**
   * エラーログを外部サービスに送信
   *
   * 優先順位:
   * 1. Sentry（設定されている場合）
   * 2. バックエンドのログエンドポイント（/api/log-error）
   * 3. localStorageにフォールバック（オフライン時や送信失敗時）
   */
  private logErrorToService(error: Error, errorInfo: ErrorInfo, errorId: string): void {
    // Sentryが設定されている場合は送信
    if (typeof window !== 'undefined' && (window as any).Sentry) {
      try {
        (window as any).Sentry.captureException(error, {
          tags: { errorId },
          contexts: { react: errorInfo }
        });
        console.log('✅ Sentryにエラーを送信しました:', errorId);
        return; // Sentry送信成功したら終了
      } catch (sentryError) {
        console.error('⚠️ Sentry送信に失敗:', sentryError);
        // フォールバックに続行
      }
    }

    // フォールバック: バックエンドのログエンドポイントへPOST
    // 注: /api/log-error エンドポイントが実装されている必要があります
    const errorPayload = {
      errorId,
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      url: window.location.href
    };

    fetch('/api/log-error', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(errorPayload)
    })
      .then(response => {
        if (response.ok) {
          console.log('✅ エラーログをバックエンドに送信しました:', errorId);
        } else {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
      })
      .catch(logError => {
        // エラーログ送信失敗時の詳細ログ
        console.error('❌ エラーログの送信に失敗:', {
          errorId: 'ERROR_LOGGING_FAILED',
          originalError: error.message,
          loggingError: logError instanceof Error ? logError.message : String(logError),
          timestamp: new Date().toISOString()
        });

        // localStorageにフォールバック保存（最大10件）
        // オフライン時や送信失敗時の証跡を残す
        this.saveErrorToLocalStorage(errorPayload, logError);
      });
  }

  /**
   * エラーログをlocalStorageに保存
   *
   * 送信に失敗したエラーログを一時保存し、
   * 後で手動確認や再送信できるようにする
   */
  private saveErrorToLocalStorage(errorPayload: any, loggingError: any): void {
    try {
      let failedLogs: any[];
      try {
        const stored = localStorage.getItem('failedErrorLogs');
        failedLogs = JSON.parse(stored || '[]');
      } catch (parseError) {
        // 不正なJSONの場合は空配列からスタート
        failedLogs = [];
      }

      failedLogs.push({
        ...errorPayload,
        loggingError: loggingError instanceof Error ? loggingError.message : String(loggingError),
        savedAt: new Date().toISOString()
      });
      // 最新10件のみ保持（容量制限対策）
      const recentLogs = failedLogs.slice(-10);
      localStorage.setItem('failedErrorLogs', JSON.stringify(recentLogs));
      console.log('💾 エラーログをlocalStorageに保存しました（送信失敗時のフォールバック）');
    } catch (storageError) {
      // localStorage保存も失敗した場合（容量超過、プライベートモードなど）
      console.error('❌ localStorageへの保存も失敗:', {
        errorId: 'LOCALSTORAGE_SAVE_FAILED',
        storageError: storageError instanceof Error ? storageError.message : String(storageError)
      });
    }
  }

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            bgcolor: 'background.default',
          }}
        >
          <Alert severity="error" sx={{ mb: 2, maxWidth: 600 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              アプリケーションエラーが発生しました
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              ページをリロードしてください。問題が解決しない場合は管理者に連絡してください。
            </Typography>
            {this.state.errorId && (
              <>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    color: 'text.secondary',
                    mt: 1,
                  }}
                >
                  エラーID: {this.state.errorId}
                  {process.env.NODE_ENV === 'production' && ' (このIDを管理者に伝えてください)'}
                </Typography>
                {process.env.NODE_ENV === 'development' && this.state.error && (
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      color: 'text.secondary',
                      mt: 0.5,
                    }}
                  >
                    エラー詳細（開発環境のみ表示）: {this.state.error.message}
                  </Typography>
                )}
              </>
            )}
          </Alert>
          <Button variant="contained" onClick={this.handleReload} sx={{ mt: 2 }}>
            ページをリロード
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
